import type { PGD } from "../../types";

export const pgdAhtWtTirzepatide: PGD = {
  id: "pgd-aht-wt-tirzepatide",
  reference: "AHT-WT-TZP",
  version: "1.4",
  drugRef: "tirzepatide",
  indication: "Weight management — tirzepatide (Mounjaro®)",
  validFrom: "",
  expiryDate: "",
  frameworkType: "other",
  service: "aht_private",
  commissioner: "Allied Health Training Limited",
  status: "active",

  population: {
    ageRange: { min: 18, max: 84 },
    sex: "any",
    pregnancyAllowed: false,
    breastfeedingAllowed: false,
    notes: "No dose adjustment required for age, sex, ethnicity, or body weight.",
  },

  consultationRequirements: {
    initialSupply: "face_to_face",
    subsequentSupply: "face_to_face",
  },

  duration: {
    days: 28,
    comment: "28-day supply per dose step (1 pen = 4 doses). Ongoing monthly review.",
  },

  doseBands: [
    {
      id: "tzp-step1",
      doseStepId: "tzp-2_5mg",
      resolveBy: "single",
      dose: 2.5,
      frequency: "Once weekly subcutaneous injection",
      comment: "Weeks 1–4: starting dose",
      formulations: [
        {
          formulationId: "tirzepatide-2_5mg-pen",
          instruction: "Inject 2.5mg subcutaneously once weekly",
          quantity: { count: 1, label: "1 × Mounjaro 2.5mg KwikPen (4 doses)" },
          allowQuantityOverride: true,
          quantityOverrideBounds: { min: 1, max: 3 },
        },
      ],
    },
    {
      id: "tzp-step2",
      doseStepId: "tzp-5mg",
      resolveBy: "single",
      dose: 5,
      frequency: "Once weekly subcutaneous injection",
      comment: "Weeks 5–8: +2.5mg if tolerated",
      formulations: [
        {
          formulationId: "tirzepatide-5mg-pen",
          instruction: "Inject 5mg subcutaneously once weekly",
          quantity: { count: 1, label: "1 × Mounjaro 5mg KwikPen (4 doses)" },
          allowQuantityOverride: true,
          quantityOverrideBounds: { min: 1, max: 3 },
        },
      ],
    },
    {
      id: "tzp-step3",
      doseStepId: "tzp-7_5mg",
      resolveBy: "single",
      dose: 7.5,
      frequency: "Once weekly subcutaneous injection",
      comment: "Weeks 9–12: +2.5mg if tolerated",
      formulations: [
        {
          formulationId: "tirzepatide-7_5mg-pen",
          instruction: "Inject 7.5mg subcutaneously once weekly",
          quantity: { count: 1, label: "1 × Mounjaro 7.5mg KwikPen (4 doses)" },
          allowQuantityOverride: true,
          quantityOverrideBounds: { min: 1, max: 3 },
        },
      ],
    },
    {
      id: "tzp-step4",
      doseStepId: "tzp-10mg",
      resolveBy: "single",
      dose: 10,
      frequency: "Once weekly subcutaneous injection",
      comment: "Weeks 13–16: +2.5mg if tolerated",
      formulations: [
        {
          formulationId: "tirzepatide-10mg-pen",
          instruction: "Inject 10mg subcutaneously once weekly",
          quantity: { count: 1, label: "1 × Mounjaro 10mg KwikPen (4 doses)" },
          allowQuantityOverride: true,
          quantityOverrideBounds: { min: 1, max: 3 },
        },
      ],
    },
    {
      id: "tzp-step5",
      doseStepId: "tzp-12_5mg",
      resolveBy: "single",
      dose: 12.5,
      frequency: "Once weekly subcutaneous injection",
      comment: "Weeks 17–20: +2.5mg if tolerated",
      formulations: [
        {
          formulationId: "tirzepatide-12_5mg-pen",
          instruction: "Inject 12.5mg subcutaneously once weekly",
          quantity: { count: 1, label: "1 × Mounjaro 12.5mg KwikPen (4 doses)" },
          allowQuantityOverride: true,
          quantityOverrideBounds: { min: 1, max: 3 },
        },
      ],
    },
    {
      id: "tzp-step6",
      doseStepId: "tzp-15mg",
      resolveBy: "single",
      dose: 15,
      frequency: "Once weekly subcutaneous injection",
      comment: "Weeks 21+: maximum dose, if tolerated",
      formulations: [
        {
          formulationId: "tirzepatide-15mg-pen",
          instruction: "Inject 15mg subcutaneously once weekly",
          quantity: { count: 1, label: "1 × Mounjaro 15mg KwikPen (4 doses)" },
          allowQuantityOverride: true,
          quantityOverrideBounds: { min: 1, max: 3 },
        },
      ],
    },
  ],

  preSupplyChecks: [
    {
      id: "inc-age-18-84",
      question: "Is the patient aged 18–84 years?",
      direction: "exclude_on_no",
      autoAnswerFrom: "age_18_to_84",
    },
    {
      id: "inc-bmi-meets-threshold",
      question: "Does the patient meet the BMI inclusion threshold? (BMI ≥30; or BMI ≥27 with ≥1 weight-related comorbidity; or BMI ≥27.5 in South Asian, Chinese, other Asian, Middle Eastern, Black African, or African-Caribbean backgrounds)",
      direction: "exclude_on_no",
      autoAnswerFrom: "sharedBmiInclusionMet",
    },
    {
      id: "inc-monitoring-consent",
      question: "Has the patient consented to monthly monitoring and review?",
      direction: "exclude_on_no",
      autoAnswerFrom: "sharedMonitoringConsent",
    },
    {
      id: "inc-lifestyle-agreement",
      question: "Has the patient agreed to a reduced-calorie diet and increased physical activity?",
      direction: "exclude_on_no",
      autoAnswerFrom: "sharedLifestyleAgreement",
    },
    {
      id: "inc-informed-consent",
      question: "Has the patient provided informed consent?",
      direction: "exclude_on_no",
      autoAnswerFrom: "sharedInformedConsent",
    },
    {
      id: "inc-face-to-face",
      question: "Is the patient attending face-to-face at registered premises?",
      hint: "No virtual or online consultations permitted under this PGD.",
      direction: "exclude_on_no",
      autoAnswerFrom: "sharedFaceToFace",
    },
    {
      id: "ex-bmi-below-27",
      question: "Is the patient's BMI below 27 kg/m² (and not already established on Mounjaro®)?",
      autoAnswerFrom: "bmiBelowThreshold",
    },
    {
      id: "ex-bmi-27-30-no-comorbidity",
      question: "Is the patient's BMI 27–30 kg/m² without a weight-related comorbidity?",
      autoAnswerFrom: "bmiInLowRangeNoComorbidity",
    },
    {
      id: "ex-bmi-below-22-during-treatment",
      question: "Has the patient's BMI fallen below 22 kg/m² during treatment?",
      autoAnswerFrom: "bmiBelowFloor",
    },
    {
      id: "ex-hypersensitivity",
      question: "Does the patient have known hypersensitivity to tirzepatide or any excipient, or a previous anaphylactic reaction to tirzepatide or any GLP-1 agonist?",
    },
    {
      id: "ex-t2dm-hba1c-high",
      question: "Does the patient have Type 2 diabetes with HbA1c ≥86 mmol/mol?",
      autoAnswerFrom: "t2dmHba1cHigh",
    },
    {
      id: "ex-sulphonylureas-insulin",
      question: "Is the patient currently using sulphonylureas or insulin?",
      autoAnswerFrom: "q6SulphonylureaInsulin",
    },
    {
      id: "ex-concurrent-weight-loss-glp1",
      question: "Is the patient currently using other weight loss agents or GLP-1 agonists?",
      autoAnswerFrom: "sharedConcurrentWeightAgent",
    },
    {
      id: "ex-endocrine-obesity",
      question: "Is the patient's obesity secondary to an endocrine condition or weight-gain medications?",
      autoAnswerFrom: "sharedEndocrineObesity",
    },
    {
      id: "ex-bmi-monitoring-refusal",
      question: "Is the patient refusing to participate in BMI monitoring?",
      autoAnswerFrom: "sharedMonitoringRefusal",
    },
  ],

  interactions: [
    {
      id: "int-narrow-therapeutic-index",
      interactionName: "Narrow therapeutic index oral medicines",
      question: "Is the patient taking any oral medicines with a narrow therapeutic index?",
      hint: "Tirzepatide delays gastric emptying — may affect absorption of oral medicines.",
      affectedDrugs: ["tirzepatide"],
      excludeIfUnmanageable: false,
      managementPlanRequired: true,
    },
    {
      id: "int-oral-contraceptives",
      interactionName: "Oral contraceptives",
      question: "Is the patient using oral contraceptives?",
      hint: "Reduced efficacy cannot be excluded — add barrier method for 4 weeks after starting or any dose increase.",
      affectedDrugs: ["tirzepatide"],
      excludeIfUnmanageable: false,
      managementPlanRequired: true,
    },
  ],

  cautions: [
    { id: "caut-gi-dehydration", text: "GI symptoms (nausea, vomiting, diarrhoea) — dehydration risk" },
    { id: "caut-gastroparesis", text: "Gastroparesis symptoms (bloating, abdominal pain) — discontinue if develops" },
    { id: "caut-allergic-reaction", text: "Suspected allergic reaction — discontinue" },
    { id: "caut-pancreatitis", text: "Pancreatitis suspected — stop immediately; do not restart if confirmed" },
    { id: "caut-heart-rate", text: "Monitor resting heart rate" },
    { id: "caut-mood-changes", text: "Report mood or behavioural changes" },
    { id: "caut-not-insulin-substitute", text: "Not a substitute for insulin" },
    {
      id: "caut-aspiration",
      text: "Aspiration risk — advise of delayed gastric emptying before GA or deep sedation",
      adviceText: "Patient should inform anaesthetist/surgical team before any general anaesthetic or deep sedation.",
    },
    { id: "caut-renal-gi-effects", text: "Renal function may deteriorate with prolonged GI effects" },
  ],

  counselling: [
    "Inject subcutaneously into abdomen, thigh, or upper arm — rotate sites at each injection",
    "Store 2–8°C; protect from light; do not freeze",
    "After first use: may store ≤30°C for up to 30 days",
    "Discard if frozen, discoloured, or expired",
    "Check cold chain and pen integrity before supply",
    "Record batch number and expiry date",
    "Provide PIL; explain mechanism and pen use; supervise first dose if needed",
    "Advise patient to inform GP on initiation and at each dose change",
    "Pancreatitis warning: abdominal pain, fever, nausea, vomiting, jaundice — attend A&E",
    "Encourage adequate fluid intake with GI side effects",
    "Oral contraceptives: add barrier method for 4 weeks after starting or any dose increase",
    "Documentation retained 8 years",
  ],

  efficacyReview: {
    timepoint: "6 months at maximum tolerated dose",
    threshold: "≥5% weight loss",
    actionIfNotMet: "Discontinue — stop treatment, refer to GP, offer lifestyle support",
  },

  monitoring: {
    frequency: "monthly",
    parameters: [
      "Weight",
      "BMI",
      "Waist circumference (strongly recommended)",
      "Side effects",
      "HbA1c if Type 2 diabetes",
      "Titrate dose every 4+ weeks based on response and tolerance",
    ],
    discontinueIf: [
      "Less than 5% weight loss after 6 months at maximum tolerated dose",
    ],
  },

  reviewSchedule: {
    supplyIntervalDays: 28,
    drugSpecificReviewDays: 168,
    drugSpecificReviewLabel: "6-month efficacy review",
    efficacyThresholdPercent: 5,
    stopRuleDescription: "Discontinue if <5% weight loss after 6 months at maximum tolerated dose — stop treatment, refer to GP, offer lifestyle support",
    sectionBReviewDays: 168,
    bmiFloorKgM2: 22.0,
    titrationSchedule: undefined,
    maxEpisodesBeforeReview: null,
    requiresBpPulseAtEscalation: false,
    requiresBrandAndBatchAtEverySupply: false,
  },

  adverseEventGuidance: [
    "Anaphylaxis/angioedema: STOP immediately — refer urgently",
    "Pancreatitis suspected: STOP immediately — do not restart if confirmed",
    "Gastroparesis symptoms: discontinue",
    "Dehydration with GI effects: review; advise fluid intake",
    "Report locally, notify GP, MHRA Yellow Card (yellowcard.mhra.gov.uk)",
    "Black Triangle medicine — ALL suspected ADRs must be reported",
  ],
};
